import React, { useState } from 'react';
import { X, ArrowRight, ArrowLeft, Landmark, Split, Target, Scale, Rocket } from 'lucide-react';

function Onboarding({ onComplete, onSkip }) {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      icon: Rocket,
      iconTone: 'from-blue-500 to-indigo-600',
      title: 'Welcome to your financial operating system',
      description: 'This app helps you route income, allocate it automatically, and stay on track every week.',
      bullets: [
        'Income hub',
        'Automatic allocation',
        'Weekly target',
        'Rebalancing',
      ],
    },
    {
      icon: Landmark,
      iconTone: 'from-blue-500 to-cyan-600',
      title: 'Your income starts at the hub',
      description: 'Every dollar flows into your Business Account first, then gets split across Personal, Business, Taxes, and Savings.',
      bullets: [
        'One entry point for every deposit',
        'Clean paper trail from day one',
        'No more guessing where money goes',
      ],
    },
    {
      icon: Target,
      iconTone: 'from-emerald-500 to-teal-600',
      title: 'Know your number',
      description: 'Your Weekly Target shows the number you need to hit to stay stable, grow, or catch up.',
      bullets: [
        'Survival — cover this week\'s bills',
        'Growth — fully fund your plan',
        'Catch-Up — close historical gaps at a realistic pace',
      ],
    },
    {
      icon: Scale,
      iconTone: 'from-purple-500 to-fuchsia-600',
      title: 'Stay aligned automatically',
      description: 'If your accounts drift off target, rebalancing helps move money where it is needed most.',
      bullets: [
        'Priority routing: Taxes → Personal → Business → Savings',
        'Minimum balance protections',
        'Instant fix or weekly catch-up — your call',
      ],
    },
    {
      icon: Split,
      iconTone: 'from-orange-500 to-rose-600',
      title: 'You\'re ready. Let\'s build your system.',
      description: 'Set your allocation percentages, log your first income, and your system starts working for you.',
      bullets: [
        '1. Set your allocation plan',
        '2. Log your first weekly income',
        '3. Watch your accounts auto-allocate',
      ],
    },
  ];

  const step = steps[currentStep];
  const Icon = step.icon;
  const isLast = currentStep === steps.length - 1;

  const handleNext = () => {
    if (!isLast) setCurrentStep(currentStep + 1);
    else onComplete();
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="onboarding-modal">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden">
        {/* Header */}
        <div className={`bg-gradient-to-r ${step.iconTone} text-white p-6`}>
          <div className="flex justify-between items-start">
            <div className="flex items-start gap-3">
              <div className="bg-white/15 backdrop-blur rounded-lg p-2 flex-shrink-0">
                <Icon className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold leading-tight">{step.title}</h2>
              </div>
            </div>
            <button
              onClick={onSkip}
              className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
              aria-label="Skip onboarding"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Progress */}
          <div className="mt-5">
            <div className="flex justify-between text-xs text-white/80 mb-1.5">
              <span>Step {currentStep + 1} of {steps.length}</span>
              <span>{Math.round(((currentStep + 1) / steps.length) * 100)}%</span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-1.5">
              <div
                className="bg-white rounded-full h-1.5 transition-all duration-300"
                style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          <p className="text-base text-gray-700 dark:text-gray-200 leading-relaxed mb-6">
            {step.description}
          </p>
          <ul className="space-y-2">
            {step.bullets.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
                <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-6 flex justify-between items-center bg-gray-50 dark:bg-gray-900">
          <button
            onClick={onSkip}
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium text-sm"
            data-testid="onboarding-skip"
          >
            Skip
          </button>
          <div className="flex gap-3">
            {currentStep > 0 && (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 px-5 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-sm"
                data-testid="onboarding-back"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors text-sm"
              data-testid="onboarding-next"
            >
              {isLast ? 'Build my system' : 'Next'}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Onboarding;
